import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, inject, Input, OnInit, signal, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { AddDestinationAlternateModalComponent } from '@components/add-destination-alternate-modal/add-destination-alternate-modal.component';
import { AddDestinationModalComponent } from '@components/add-destination-modal/add-destination-modal.component';
import { AddDestinationPartialSuccessModalComponent } from '@components/add-destination-partial-success-modal/add-destination-partial-success-modal.component';
import { ImmutabilityNotDefinedComponent } from '@components/immutability-modals/immutability-not-defined/immutability-not-defined.component';
import {
  CheckerError,
  CreationMode,
  e2eLocators,
  getErrorMap,
  helpLinkMap
} from '@components/storage-accounts/add-storage-account-wizard/add-storage-account-wizard.constants';
import { PrivateStorageModalComponent } from '@components/storage-accounts/add-storage-account-wizard/components/private-storage-modal/private-storage.component';
import { StorageFormName } from '@components/storage-accounts/add-storage-account-wizard/credential-forms/credential-forms.constants';
import { CredentialFormsConstructor } from '@components/storage-accounts/add-storage-account-wizard/credential-forms/credential-forms.constructor';
import { CredentialForm } from '@components/storage-accounts/add-storage-account-wizard/credential-forms/credential-forms.model';
import { AmazonAdvancedSettingsComponent } from '@components/storage-accounts/add-storage-account-wizard/steps/amazon-advanced-settings/amazon-advanced-settings.component';
import { CompaniesStepComponent } from '@components/storage-accounts/add-storage-account-wizard/steps/companies/companies-step.component';
import { S3CompatibleAdvancedSettingsComponent } from '@components/storage-accounts/add-storage-account-wizard/steps/s3-compatible-advanced-settings/s3-compatible-advanced-settings.component';
import { SelectStorageStepComponent } from '@components/storage-accounts/add-storage-account-wizard/steps/select-storage/select-storage-step.component';
import { SetDestinationStepComponent } from '@components/storage-accounts/add-storage-account-wizard/steps/set-destination/set-destination-step.component';
import { StorageAccountStepComponent } from '@components/storage-accounts/add-storage-account-wizard/steps/storage-account/storage-account-step.component';
import { SummaryStepComponent } from '@components/storage-accounts/add-storage-account-wizard/steps/summary/summary-step.component';
import { UbAwsChangingPriceModalComponent } from '@components/storage-accounts/ub-aws-changing-price-modal/ub-aws-changing-price-modal.component';
import { UbSubscribeService } from '@components/storage-accounts/ub-subscribe.service';
import { ubStorageTypes } from '@models/storage-accounts/storages.constants';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AbilityService } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import { isNil, noop, omitBy } from 'lodash';
import { FormsUtil, MbsSize, ModalService, TagInputItem, ToastService, WizardComponent, WizardStep, WizardStepComponent } from 'mbs-ui-kit';
import { StorageType } from 'mbs-ui-shared';
import {
  Destination,
  S3AuthType,
  SetStorageType,
  StorageAccountV2,
  StorageAccountV2ListUnit
} from 'mbs-ui-shared/src/lib/models/StorageAccount';
import { StorageAccountsService } from 'mbs-ui-shared/src/lib/services/storage-accounts/storage-accounts.service';
import { ReportProblemModalComponent } from 'mbs-ui-shared@components/report-problem-modal/report-problem-modal.component';
import { ErrorCode } from 'mbs-ui-shared@constants/errorCodes';
import {
  amazonS3GlacierStorageTypes,
  arnRoleStorageTypes,
  ftpStorageTypes,
  getBucketTypeName,
  s3CompatibleStorages
} from 'mbs-ui-shared@constants/storage.constants';
import { StorageAccountsFacade } from 'mbs-ui-shared@facades/storage-accounts.facade';
import { StorageDestinationsFacade } from 'mbs-ui-shared@facades/storage-destinations.facade';
import { unidentifiedErrorText } from 'mbs-ui-shared@interceptors/error-handler.interceptor';
import { Company, PermissionsEnum } from 'mbs-ui-shared@models';
import { CurrentCredits } from 'mbs-ui-shared@models/storage-accounts/CurrentCredits';
import {
  BucketInfo,
  GoogleStorageClassEnum,
  StoragesAvailableForCreation,
  StorageTypeState,
  StorageWizardCompanies
} from 'mbs-ui-shared@models/StorageAccount';
import { generateUid } from 'mbs-ui-shared@utils/generateUid';
import { EMPTY, from, of, repeat, Subject, takeUntil } from 'rxjs';
import { catchError, filter, finalize, first, switchMap, take, tap } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'mbs-add-storage-account-wizard',
  templateUrl: './add-storage-account-wizard.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AmazonAdvancedSettingsComponent,
    CompaniesStepComponent,
    I18NextPipe,
    StorageAccountStepComponent,
    S3CompatibleAdvancedSettingsComponent,
    SelectStorageStepComponent,
    SetDestinationStepComponent,
    SummaryStepComponent,
    WizardComponent,
    WizardStepComponent
  ]
})
export class AddStorageAccountWizardComponent implements OnInit {
  private fb = inject(FormBuilder);
  private storageAccountsService = inject(StorageAccountsService);
  private accountsFacade = inject(StorageAccountsFacade);
  private destinationsFacade = inject(StorageDestinationsFacade);
  private cdr = inject(ChangeDetectorRef);
  private toastService = inject(ToastService);
  private i18N = inject(I18NextPipe);
  private modalService = inject(ModalService);
  private ubSubscribeService = inject(UbSubscribeService);
  private ability = inject(AbilityService);

  @ViewChild(WizardComponent, { static: true }) wizard: WizardComponent;

  public readonly isNil = isNil;
  public readonly storageType = StorageType;
  public readonly authType = S3AuthType;
  public readonly creationMode = CreationMode;
  public readonly errorMap = getErrorMap(this.i18N);

  public isCredentialsValid = signal(false);
  public isS3CompatibleChecked = signal(false);
  public isS3CompatibleOptionsValid = signal(false);
  public credentialsError: string = null;
  public saveError = null;

  public isNextButtonLoading = signal(false);
  public isLoading = signal(false);
  public availableStorages: StoragesAvailableForCreation;
  // Launched from a company context: the companies step is hidden, the company initializes both the account scope and the destination
  @Input() companyIds: string[];

  public wizardCompanies = signal<StorageWizardCompanies | null>(null);
  // An Any-company account has an empty scope, but its destination companies are de facto assigned:
  // restricting such an account must start from them, not from an empty yet locked selection
  public readonly preselectedCompanyIds = computed(() => {
    const data = this.wizardCompanies();

    return data?.companyIds?.length ? data.companyIds : (data?.destinationCompanyIds ?? []);
  });
  public wizardCompaniesLoading = signal(false);
  public restrictByCompaniesControl = new FormControl<boolean>(false);
  public wizardCompaniesControl = new FormControl<Company[]>([]);
  private savedCompanyIds: string[] = [];

  public selectStorageError = signal<string | null>(null);
  public existingAccounts = signal<StorageAccountV2ListUnit[]>([]);
  public existingAccountsLoading = signal(false);
  public existingAccountsLoaded = signal(false);
  public canSelectSource = signal(false);
  public selectedAccountId = signal<string>(null);
  public existingAccountLoaded = signal(false);
  // The picked row is kept apart from wizardForm.storageAccount: the destination form locks onto the first account it
  // gets and ignores later ones with the same id, so it must never see the list unit
  public pickedAccount = new FormControl<StorageAccountV2ListUnit>(null);
  private existingAccountsTotal = 0;
  private readonly existingAccountsPageSize = 20;
  private readonly cancelCompatibilityCheck$ = new Subject<void>();
  public currentBucket: BucketInfo = null;
  public bucketInfoLoading = signal(false);
  public storageId = null;
  public isNativeMultipartControlShown = signal(false);
  public isMBSIamRole = signal(false);
  public selectStorageStepValid = signal(false);
  public showS3AdditionalOptions = signal(false);
  public cover = signal(null);
  public helpLink = null;
  public compatibleError = signal(null);

  public wizardForm = this.fb.group({
    accountDisplayName: new FormControl(null, [Validators.required, Validators.maxLength(100)]),
    storageType: this.fb.control<StorageType>(null),
    storageAccount: this.fb.control<StorageAccountV2 | StorageAccountV2ListUnit>(null),
    createNew: this.fb.control(true),
    useExistingAccount: this.fb.control(false),
    destination: this.fb.group({
      displayBucketName: new FormControl('', [Validators.required]),
      bucketName: new FormControl('', [Validators.required]),
      region: new FormControl(null),
      isGfsObjectLockEnabled: new FormControl(null),
      objectLockPeriodDays: new FormControl(null),
      storageClass: new FormControl(null),
      googleDestinationSubregion: new FormControl(null),
      companies: new FormControl([]),
      storageCloudAccountId: new FormControl(null),
      s3Accelerator: new FormControl(null),
      isExternalBucket: new FormControl(null)
    }),
    credentialsInfo: new FormGroup({
      [StorageFormName[StorageType.AmazonS3]]: CredentialFormsConstructor.create(StorageType.AmazonS3),
      [StorageFormName[StorageType.BackblazeB2]]: CredentialFormsConstructor.create(StorageType.BackblazeB2),
      [StorageFormName[StorageType.FTP]]: CredentialFormsConstructor.create(StorageType.FTP),
      [StorageFormName[StorageType.SFTP]]: CredentialFormsConstructor.create(StorageType.SFTP),
      [StorageFormName[StorageType.GoogleCloud]]: CredentialFormsConstructor.create(StorageType.GoogleCloud),
      [StorageFormName[StorageType.Azure]]: CredentialFormsConstructor.create(StorageType.Azure),
      [StorageFormName[StorageType.Minio]]: CredentialFormsConstructor.create(StorageType.Minio),
      [StorageFormName[StorageType.S3Compatible]]: CredentialFormsConstructor.create(StorageType.S3Compatible),
      [StorageFormName[StorageType.Wasabi]]: CredentialFormsConstructor.create(StorageType.Wasabi),
      [StorageFormName[StorageType.AzureVM]]: CredentialFormsConstructor.create(StorageType.AzureVM),
      [StorageFormName[StorageType.IDriveE2]]: CredentialFormsConstructor.create(StorageType.IDriveE2),
      [StorageFormName[StorageType.LocalFS]]: CredentialFormsConstructor.create(StorageType.LocalFS),
      [StorageFormName[StorageType.GoogleDriveOAuth]]: CredentialFormsConstructor.create(StorageType.GoogleDriveOAuth),
      [StorageFormName[StorageType.GoogleDrive]]: CredentialFormsConstructor.create(StorageType.GoogleDrive),
      [StorageFormName[StorageType.OpenStack]]: CredentialFormsConstructor.create(StorageType.OpenStack),
      [StorageFormName[StorageType.ImpossibleCloud]]: CredentialFormsConstructor.create(StorageType.ImpossibleCloud)
    }),
    arnRoleOptions: this.fb.group({
      restrictBuckets: new FormControl(false),
      specificBuckets: new FormControl([])
    }),
    s3CompatibleFlags: new FormControl(null)
  });
  public bucketTypeName = null;

  public destinationForm: FormGroup;
  public createNewControl: FormControl;
  public mode: CreationMode = CreationMode.Create;
  public storage: StorageAccountV2ListUnit;
  public lockedCompany: Company;

  public e2eLocators = e2eLocators;

  get accountDisplayName(): FormControl {
    return this.wizardForm.get('accountDisplayName') as FormControl;
  }

  get storageAccount(): FormControl {
    return this.wizardForm.get('storageAccount') as FormControl;
  }

  get useExistingAccountControl(): FormControl {
    return this.wizardForm.get('useExistingAccount') as FormControl;
  }

  get isUsingExistingAccount(): boolean {
    return this.mode === CreationMode.Create && this.useExistingAccountControl.value;
  }

  get needsS3CompatibilityCheck(): boolean {
    if (this.storageTypeValue !== StorageType.S3Compatible || this.isUsingExistingAccount) return false;

    return !this.isPrivateStorageControl?.value;
  }

  get currentCredentialsForm() {
    return this.wizardForm.get('credentialsInfo')?.get(StorageFormName[this.wizardForm?.value?.storageType]);
  }

  get isPrivateStorageControl() {
    return this.currentCredentialsForm?.get('isPrivateStorage');
  }

  get ignoreCertificateControl() {
    return this.currentCredentialsForm?.get('ignoreCertificate');
  }

  get useSignatureVersion4Control() {
    return this.currentCredentialsForm?.get('useSignatureVersion4');
  }

  private readonly isS3CompatibleLike = [...s3CompatibleStorages, StorageType.Minio];

  ngOnInit() {
    this.createNewControl = this.wizardForm.get('createNew') as FormControl;
    this.destinationForm = this.wizardForm.get('destination') as FormGroup;

    this.addSubscriptions();

    if (this.mode === CreationMode.Create) {
      this.applyCreateMode();
    } else {
      this.applyEditMode();
    }
  }

  get credentialsInfo(): FormGroup {
    return this.wizardForm.get('credentialsInfo') as FormGroup;
  }

  get s3CompatibleForm(): FormGroup {
    return this.credentialsInfo.get('s3Compatible') as FormGroup;
  }

  get credentialsForm(): FormGroup {
    return this.credentialsInfo?.get(StorageFormName[this.storageTypeValue]) as FormGroup;
  }

  get storageTypeValue(): StorageType {
    return this.wizardForm.get('storageType')?.value;
  }

  get isEditS3CompatibleOptions(): boolean {
    return this.mode === CreationMode.Edit && this.storageTypeValue === StorageType.S3Compatible;
  }

  get s3CompatibleOptionsStepValid(): boolean {
    return this.isEditS3CompatibleOptions ? this.isS3CompatibleOptionsValid() : this.isCredentialsValid();
  }

  // Account scope for the destination step: the context company or what the companies step saved; [] = All companies
  // The companies step is skipped for an existing account, so the lock has to reach the destination step on its own
  public get accountScopeCompanyIds(): string[] {
    if (!this.lockedCompany) return this.savedCompanyIds;

    return Array.from(new Set([...(this.companyIds?.length ? this.companyIds : this.savedCompanyIds), this.lockedCompany.id]));
  }

  public get companiesStepTitle(): string {
    return this.i18N.transform('shared:captions.companies', { format: 'title' });
  }

  public get selectedCompanyIdsValue(): string[] {
    return (this.wizardCompaniesControl.value ?? []).map((company) => company.id);
  }

  public get companiesStepValid(): boolean {
    const data = this.wizardCompanies();

    if (!data) return false;
    if (!data.canRestrictByCompanies) return true;
    if (this.restrictByCompaniesControl.value && !this.selectedCompanyIdsValue.length) return false;

    return !this.isCompaniesSelectionChanged;
  }

  // The PUT only stores the selection in the wizard session, so an unchanged step may be skipped entirely
  private get isCompaniesSelectionChanged(): boolean {
    const current = this.restrictByCompaniesControl.value ? this.selectedCompanyIdsValue : [];

    return current.length !== this.savedCompanyIds.length || current.some((id) => !this.savedCompanyIds.includes(id));
  }

  public onStepSelected(step: WizardStep): void {
    if (step?.title === this.companiesStepTitle) this.loadWizardCompanies();
  }

  private saveContextCompany(): void {
    this.storageAccountsService.saveWizardCompanies(this.storageId, this.companyIds).subscribe();
  }

  private loadWizardCompanies(): void {
    if (this.wizardCompaniesLoading()) return;

    this.wizardCompaniesLoading.set(true);
    this.storageAccountsService
      .getWizardCompanies(this.storageId)
      .pipe(finalize(() => this.wizardCompaniesLoading.set(false)))
      .subscribe((data) => {
        this.wizardCompanies.set(data);
        this.savedCompanyIds = data.companyIds ?? [];
        if (this.mode === CreationMode.Create) {
          this.restrictByCompaniesControl.setValue(!this.ability.can('read', PermissionsEnum.AccessToAllCompanies), { emitEvent: false });
        } else {
          this.restrictByCompaniesControl.setValue(!!this.savedCompanyIds.length, { emitEvent: false });
        }

        if (!data.canRestrictByCompanies || !this.ability.can('read', PermissionsEnum.AccessToAllCompanies)) {
          this.restrictByCompaniesControl.disable({ emitEvent: false });
          this.wizardCompaniesControl.disable({ emitEvent: false });
        }

        this.applyLockedCompany(data);
      });
  }

  // Runs after loadWizardCompanies, which resets the radio from the server response and would otherwise overwrite the lock
  private applyLockedCompany(data: StorageWizardCompanies): void {
    if (!this.lockedCompany || !data.canRestrictByCompanies) return;

    this.restrictByCompaniesControl.setValue(true, { emitEvent: false });
    this.wizardCompaniesControl.setValue([this.lockedCompany], { emitEvent: false });
    this.restrictByCompaniesControl.disable({ emitEvent: false });
    this.wizardCompaniesControl.disable({ emitEvent: false });
  }

  public submitWizardCompanies(): void {
    const data = this.wizardCompanies();

    if (!data?.canRestrictByCompanies || !this.isCompaniesSelectionChanged) return;

    if (this.restrictByCompaniesControl.value && !this.selectedCompanyIdsValue.length) {
      return void FormsUtil.triggerValidation(this.wizardCompaniesControl);
    }

    const lockedIds = data.destinationCompanyIds ?? [];
    const ids = this.restrictByCompaniesControl.value ? Array.from(new Set([...this.selectedCompanyIdsValue, ...lockedIds])) : [];

    this.isNextButtonLoading.set(true);
    this.storageAccountsService
      .saveWizardCompanies(this.storageId, ids)
      .pipe(finalize(() => this.isNextButtonLoading.set(false)))
      .subscribe(() => {
        this.savedCompanyIds = ids;
        this.wizardCompanies.set({ ...data, companyIds: ids });
        setTimeout(() => this.wizard.changeStep(1), 0);
      });
  }

  public validateCredentials(fromS3CompatibleOptions = false) {
    if (this.isUsingExistingAccount) return void this.loadSelectedAccountDetails();

    const isS3CompatibleLike = this.isS3CompatibleLike.includes(this.wizardForm.get('storageType')?.value);
    // Edit writes the advanced options into the account only through validate, so this step always resends them
    const revalidateS3CompatibleOptions = fromS3CompatibleOptions && this.isEditS3CompatibleOptions;

    // Form has already validated
    if (
      !revalidateS3CompatibleOptions &&
      this.isCredentialsValid() &&
      this.accountDisplayName.value === this.storageAccount?.value?.displayName
    ) {
      return;
    }

    if (isS3CompatibleLike) {
      this.setS3CompatibleValidity();
    }

    if ((this.credentialsForm && !this.credentialsForm.valid) || !this.accountDisplayName.valid) {
      this.credentialsForm && FormsUtil.triggerValidation(this.credentialsForm);
      FormsUtil.triggerValidation(this.accountDisplayName);

      return;
    }

    if (this.mode === CreationMode.Create && !this.isCredentialsValid() && !fromS3CompatibleOptions) this.storageId = generateUid();

    const detectS3CompatibleOptions =
      this.storageTypeValue === StorageType.S3Compatible && !fromS3CompatibleOptions && this.mode === CreationMode.Create;

    if (detectS3CompatibleOptions) {
      this.ignoreCertificateControl.patchValue(false, { emitEvent: false });
      this.useSignatureVersion4Control.patchValue(true, { emitEvent: false });
      this.isPrivateStorageControl.patchValue(false, { emitEvent: false });
      this.showS3AdditionalOptions.set(false);
    }

    this.isNextButtonLoading.set(true);
    this.credentialsError = null;
    this.storageAccountsService
      .validateStorageWizard({
        payload: CredentialFormsConstructor.getPreparedCredentialFormValue(
          this.credentialsForm as CredentialForm,
          this.wizardForm.value.storageType,
          this.wizardForm.value.accountDisplayName,
          detectS3CompatibleOptions
        ),
        id: this.storageId,
        skipError: true
      })
      .pipe(
        tap((res) => {
          if (res) {
            this.ignoreCertificateControl.patchValue(res.ignoreCertificate, { emitEvent: false });
            this.useSignatureVersion4Control.patchValue(res.useSignatureVersion4, { emitEvent: false });
          }
        }),
        switchMap(() => (fromS3CompatibleOptions ? of(null) : this.storageAccountsService.getStorageAccount(this.storageId, true))),
        finalize(() => this.isNextButtonLoading.set(false))
      )
      .subscribe({
        next: (res) => {
          revalidateS3CompatibleOptions && this.isS3CompatibleOptionsValid.set(true);
          this.handleValidationSuccess(res, !fromS3CompatibleOptions);
        },
        error: (err: HttpErrorResponse) => this.handleValidateError(err)
      });
  }

  private handleValidationSuccess(res?: StorageAccountV2, companiesStepIsNext = true) {
    this.isCredentialsValid.set(true);
    // Emitting here would run clearOutdatedState over the state this very validation has just built
    res && this.applyStorageAccount(res, { emitEvent: false });
    this.handleCredentialChanges();

    // The wizard emits stepSelected only for manual sidebar jumps, so entering the companies step by Next is handled here
    if (companiesStepIsNext && !this.isUsingExistingAccount) {
      this.companyIds?.length ? this.saveContextCompany() : this.loadWizardCompanies();
    }

    setTimeout(() => this.wizard.changeStep(1), 0);
  }

  private handleValidateError(err: HttpErrorResponse) {
    if (err?.error?.typeCode === ErrorCode.PrivateStorage) {
      const ref = this.modalService.openRef(PrivateStorageModalComponent);
      ref.componentInstance.errorText = err?.error?.detail;

      from(ref.result)
        .pipe(
          catchError(() => of(false)),
          filter((isConfirmed) => !!isConfirmed),
          tap(() => this.isPrivateStorageControl.patchValue(true, { emitEvent: false })),
          switchMap(() =>
            this.storageAccountsService.validateStorageWizard({
              payload: CredentialFormsConstructor.getPreparedCredentialFormValue(
                this.credentialsForm as CredentialForm,
                this.wizardForm.value.storageType,
                this.wizardForm.value.accountDisplayName
              ),
              id: this.storageId,
              skipError: true
            })
          ),
          switchMap(() => this.storageAccountsService.getStorageAccount(this.storageId, true))
        )
        .subscribe((res) => {
          this.showS3AdditionalOptions.set(true);
          this.handleValidationSuccess(res);
        });
    } else {
      this.isCredentialsValid.set(false);
      this.credentialsError = err?.error?.detail;
    }
  }

  private setS3CompatibleValidity() {
    const httpEndpoint = this.credentialsForm.get('httpEndpoint');
    const httpsEndpoint = this.credentialsForm.get('httpsEndpoint');

    if (httpEndpoint?.value || httpsEndpoint?.value) {
      httpEndpoint.setErrors(null);
      httpsEndpoint.setErrors(null);
    } else {
      httpEndpoint.setErrors([{ endpointError: true }]);
      httpEndpoint?.markAsDirty();
      httpEndpoint?.markAsTouched();
      httpsEndpoint.setErrors([{ endpointError: true }]);
      httpsEndpoint?.markAsDirty();
      httpsEndpoint?.markAsTouched();
    }
    this.compatibleError.set(null);
  }

  public onSaveButtonClick() {
    // Picking an existing bucket nulls currentBucket until getBucketInfo answers — saving in that window sends an empty bucket
    if (this.bucketInfoLoading()) return;

    const { id } = this.wizardForm.get('storageAccount').value as StorageAccountV2;
    const arnRoleOptions = this.wizardForm.get('arnRoleOptions').value;
    let destination = void 0;

    if (this.mode === CreationMode.Create) {
      destination = this.getDestinationData();
    }

    const data: SetStorageType = {
      destination,
      arnRoleOptions: this.isMBSIamRole()
        ? {
            restrictBuckets: arnRoleOptions?.restrictBuckets,
            allowedBuckets: arnRoleOptions?.restrictBuckets ? arnRoleOptions?.specificBuckets?.map(({ name }) => name) : []
          }
        : null
    };

    const req$ =
      this.currentBucket && this.currentBucket.immutabilitySupported === null
        ? from(this.modalService.openCustom(ImmutabilityNotDefinedComponent))
        : of(true);

    const saveReq$ = this.isUsingExistingAccount
      ? this.destinationsFacade.createDestination({ storageAccountId: id, data: destination })
      : this.mode === CreationMode.Create
        ? this.accountsFacade.create(id, data)
        : this.accountsFacade.edit(id, data);

    req$
      .pipe(
        filter((res) => !!res),
        switchMap(() => saveReq$),
        finalize(() => {
          this.wizard.spinnerShow = false;
          this.wizard.markForCheck();
        })
      )
      .subscribe({
        next: (res) => {
          if (res?.extensions) {
            this.handlePartialSuccess(res);
          } else {
            const toastText = this.isUsingExistingAccount
              ? this.i18N.transform('storages:destinationCreatedSuccessfully')
              : this.mode === CreationMode.Create
                ? this.i18N.transform('storages:createdSuccessfully')
                : this.i18N.transform('storages:editedSuccessfully');
            this.toastService.success(toastText);
            this.wizard.activeModal.close('success');
          }
        },
        error: (err) => {
          this.saveError = err.error.detail;
        }
      });
  }

  private getDestinationData() {
    const createNew = this.createNewControl.value;
    const formValue = this.wizardForm.value;
    const { supportedStorageClass, isPrivateStorage } = this.wizardForm.get('storageAccount').value as StorageAccountV2;
    const supportedStorageClassRegion =
      formValue?.destination?.storageClass?.id === GoogleStorageClassEnum.MultiRegional
        ? formValue?.destination?.region
        : formValue?.destination?.googleDestinationSubregion;

    const region = createNew
      ? supportedStorageClass
        ? supportedStorageClassRegion
        : formValue?.destination?.region
      : this.currentBucket?.region;
    const storageType = this.wizardForm.get('storageType').value;

    return {
      displayBucketName: formValue.destination.displayBucketName,
      isGfsObjectLockEnabled: formValue.destination?.isGfsObjectLockEnabled,
      objectLockPeriodDays: formValue.destination?.objectLockPeriodDays,
      createNewBucket: isPrivateStorage || storageType === StorageType.AzureVM || this.isS3CompatibleChecked() ? false : createNew,
      googleStorageClass: createNew ? formValue.destination?.storageClass?.id : null,
      assignToCompanies: formValue.destination?.companies?.length
        ? formValue.destination.companies.map((company) => company.id)
        : (this.companyIds ?? []),
      isExternalBucket: formValue.destination?.isExternalBucket,
      bucket: {
        bucketName: createNew ? formValue.destination.bucketName : this.currentBucket?.bucketName,
        region,
        s3AcceleratorEnabled: formValue.destination?.s3Accelerator,
        cloudBucketID: createNew ? null : this.currentBucket?.cloudBucketID,
        immutabilitySupported: createNew ? true : this.currentBucket?.immutabilitySupported
      }
    };
  }

  public onBucketNameChanged() {
    if (!this.isMBSIamRole()) return;

    const buckets = this.wizardForm.value.arnRoleOptions?.specificBuckets ?? [];
    const destination = this.wizardForm.value.destination;

    if (!destination) return;

    const preparedBuckets: TagInputItem[] = [
      {
        name: destination.bucketName,
        disabled: true
      },
      ...buckets.filter((bucket: TagInputItem) => !bucket.disabled)
    ];

    this.wizardForm.get('arnRoleOptions.specificBuckets').patchValue(preparedBuckets);
  }

  public finalValidate() {
    if (!this.destinationForm.valid) {
      return void FormsUtil.triggerValidation(this.destinationForm);
    }

    if (!this.needsS3CompatibilityCheck) {
      return;
    } else if (this.showS3AdditionalOptions()) {
      if (!this.isS3CompatibleChecked()) {
        this.isS3CompatibleChecked.set(true);

        return void setTimeout(() => this.wizard.changeStep(1), 0);
      }
    } else {
      this.s3CompatibleChecker();
    }
  }

  private s3CompatibleChecker() {
    this.setCompatibleError(null);
    this.cover.set('Storage options validation...');
    const query$ = this.storageAccountsService.createS3CompatibilityCheck(this.getDestinationData(), this.storageId);

    query$
      .pipe(
        switchMap(({ status, taskId }) => {
          return status
            ? of(null)
            : this.storageAccountsService.getS3CompatibilityCheck(taskId, this.storageId).pipe(
                repeat({ delay: 2000 }),
                filter(({ status }) => !!status),
                take(1),
                untilDestroyed(this)
              );
        }),
        takeUntil(this.cancelCompatibilityCheck$),
        finalize(() => {
          this.cover.set(null);
          this.destinationForm.valueChanges.pipe(first(), untilDestroyed(this)).subscribe(() => {
            this.isS3CompatibleChecked.set(false);
            this.showS3AdditionalOptions.set(false);
            this.setCompatibleError(null);
          });
        })
      )
      .subscribe({
        next: (res) => {
          this.wizardForm.get('s3CompatibleFlags').patchValue(res.s3Compatible);
          this.isS3CompatibleChecked.set(true);
          setTimeout(() => this.wizard.changeStep(1), 0);
        },
        error: (err) => {
          this.setCompatibleError(err);
        }
      });
  }

  private addSubscriptions() {
    const arnRoleForms = [StorageType.AmazonS3, StorageType.Wasabi].map((type) =>
      this.wizardForm.get('credentialsInfo').get([StorageFormName[type]])
    );

    this.wizardForm.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      this.credentialsError = null;
      this.saveError = null;
    });

    this.wizardForm
      .get('destination.bucketName')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe(() => {
        this.onBucketNameChanged();
      });

    this.wizardForm
      .get('storageType')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe((val) => {
        this.onStorageAccountChanged(val);
      });

    this.storageAccount.valueChanges.pipe(filter(Boolean), untilDestroyed(this)).subscribe(() => this.clearOutdatedState());

    this.pickedAccount.valueChanges.pipe(untilDestroyed(this)).subscribe((account) => {
      this.selectedAccountId.set(account?.id ?? null);
      this.existingAccountLoaded.set(false);
    });

    this.useExistingAccountControl.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe((useExisting) => this.onAccountSourceChanged(useExisting));

    arnRoleForms.forEach((form) =>
      form
        .get('authType')
        .valueChanges.pipe(untilDestroyed(this))
        .subscribe((val) => {
          const storageType = this.wizardForm.get('storageType')?.value;

          this.isMBSIamRole.set(arnRoleStorageTypes.includes(storageType) && val === S3AuthType.MBS);
        })
    );
  }

  private onStorageAccountChanged(storageType: StorageType) {
    this.isCredentialsValid.set(false);
    this.selectStorageStepValid.set(!isNil(storageType));
    this.clearOutdatedState();
    if (this.mode === CreationMode.Edit || isNil(storageType)) return;

    const bucketNameControl = this.wizardForm.get('destination.bucketName');

    this.helpLink = helpLinkMap[storageType];
    this.accountDisplayName.patchValue(null);
    this.accountDisplayName.markAsUntouched();
    this.accountDisplayName.markAsPristine();
    this.accountDisplayName.updateValueAndValidity();
    this.isMBSIamRole.set(false);

    if (storageType === StorageType.UbAmazonS3 || storageType === StorageType.CblWasabi) {
      bucketNameControl.patchValue(`cloudbackup${Math.floor(Math.random() * 100000000)}`);
    } else {
      bucketNameControl.patchValue(null);
    }

    // Destination labels are built from this name, so it has to be ready before that step renders
    this.bucketTypeName = getBucketTypeName(storageType, this.i18N);

    this.loadExistingAccounts(storageType);
    this.ubAWSShowCreditsStateHandler(storageType);
  }

  private onAccountSourceChanged(useExisting: boolean): void {
    this.clearOutdatedState();
    this.isCredentialsValid.set(false);
    this.storageAccount.patchValue(null, { emitEvent: false });
    this.pickedAccount.reset(null);

    if (!useExisting) this.storageId = generateUid();
  }

  private loadSelectedAccountDetails(): void {
    const selected = this.pickedAccount.value;

    if (!selected?.id) return;
    // The step is already valid, so the wizard advances by itself — a second changeStep from here would skip a step
    if (this.existingAccountLoaded() && this.storageAccount.value?.id === selected.id) return;

    this.isNextButtonLoading.set(true);
    this.storageAccountsService
      .getStorageAccount(selected.id)
      .pipe(
        finalize(() => this.isNextButtonLoading.set(false)),
        untilDestroyed(this)
      )
      .subscribe((account) => {
        if (!account) return;

        // The details response has no credential, and empty fields would wipe what the list row carries
        const details: Partial<StorageAccountV2> = omitBy(account, isNil);

        this.applyStorageAccount({ ...selected, ...details } as StorageAccountV2, { emitEvent: false });
        this.existingAccountLoaded.set(true);
        setTimeout(() => this.wizard.changeStep(1), 0);
      });
  }

  // Destination step labels are built from the storage type, so the name has to follow every account we set
  private applyStorageAccount(account: StorageAccountV2, options?: { emitEvent: boolean }): void {
    this.storageAccount.patchValue(account, options);
    this.bucketTypeName = getBucketTypeName(account.storageType, this.i18N);
  }

  private clearOutdatedState(): void {
    // The compatibility poll repeats until a status arrives, so on error it never completes by itself.
    this.cancelCompatibilityCheck$.next();
    this.credentialsError = null;
    this.saveError = null;
    this.setCompatibleError(null);
    this.cover.set(null);
    this.isS3CompatibleChecked.set(false);
    this.showS3AdditionalOptions.set(false);
  }

  private loadExistingAccounts(storageType: StorageType): void {
    this.storageAccount.patchValue(null, { emitEvent: false });
    this.pickedAccount.reset(null);
    this.existingAccounts.set([]);
    this.existingAccountsTotal = 0;

    // The storage tiles already carry the state and the number of accounts, so the source is known before the list is fetched
    const tile = this.availableStorages?.find(({ type }) => type === storageType);
    const state = tile?.state ?? StorageTypeState.Enabled;
    const hasAccounts = !!tile?.count;
    const useExisting = state === StorageTypeState.OnlyExisting || (state === StorageTypeState.Enabled && hasAccounts);

    this.canSelectSource.set(state === StorageTypeState.Enabled && hasAccounts);
    this.useExistingAccountControl.setValue(useExisting, { emitEvent: false });
    this.existingAccountsLoaded.set(!useExisting);

    useExisting && this.fetchExistingAccountsPage(storageType, true);
  }

  public loadMoreExistingAccounts(): void {
    if (this.existingAccountsLoading() || this.existingAccounts().length >= this.existingAccountsTotal) return;

    this.fetchExistingAccountsPage(this.storageTypeValue, false);
  }

  private fetchExistingAccountsPage(storageType: StorageType, isFirstPage: boolean): void {
    this.existingAccountsLoading.set(true);

    this.storageAccountsService
      .getStorageAccounts({ storageType, offset: this.existingAccounts().length, limit: this.existingAccountsPageSize })
      .pipe(
        finalize(() => {
          this.existingAccountsLoading.set(false);
          isFirstPage && this.existingAccountsLoaded.set(true);
        }),
        untilDestroyed(this)
      )
      .subscribe((res) => {
        const page = res?.data ?? [];
        this.existingAccountsTotal = res?.total ?? page.length;
        this.existingAccounts.update((accounts) => [...accounts, ...page]);

        if (isFirstPage && this.existingAccountsTotal === 1 && page.length === 1) {
          this.pickedAccount.patchValue(page[0]);
        }
      });
  }

  private applyCreateMode() {
    this.storageId = generateUid();
  }

  private applyEditMode() {
    this.storageId = this.storage.id;
    this.wizardForm.get('storageType').patchValue(this.storage?.storageType);
    this.wizardForm.get('accountDisplayName').patchValue(this.storage?.displayName);
    this.wizardForm.get('storageAccount').patchValue(this.storage);
    // Patching the type above runs clearOutdatedState, which would drop the flag if it were raised earlier
    this.showS3AdditionalOptions.set(this.storage?.storageType === StorageType.S3Compatible);

    // UB accounts have no credential form — only the display name is editable.
    if (ubStorageTypes.includes(this.storage?.storageType)) {
      return;
    }

    const isS3CompatibleLike = this.isS3CompatibleLike.includes(this.storage.storageType);
    const storageFormName = StorageFormName[this.storage?.storageType];
    const preparedCredentials = this.storage?.credential?.[storageFormName];

    if (!this.credentialsForm) {
      throw Error(`Cannot find credential form for storageType ${this.storage?.storageType}`);
    }

    this.credentialsForm.patchValue(
      {
        accountDisplayName: this.storage?.credential?.displayName,
        ...preparedCredentials
      },
      { emitEvent: false }
    );

    if (isS3CompatibleLike && this.storage?.credential?.[storageFormName]?.useNativeMultipartUpload === false) {
      this.isNativeMultipartControlShown.set(true);
    }

    if (amazonS3GlacierStorageTypes.includes(this.storage?.storageType)) {
      this.credentialsForm.get('storageType').patchValue(this.storage?.storageType);
    }
  }

  private handlePartialSuccess(res: { extensions: Record<string, string>; instance: string }) {
    this.wizard.activeModal.close('success');
    const modal = this.modalService.openRef(AddDestinationPartialSuccessModalComponent);
    modal.componentInstance.extensions = Object.values(res.extensions);

    modal.result
      .then((edit) => {
        edit && this.editDestination(res.instance);
      })
      .catch(() => noop);
  }

  private editDestination(destinationId: Destination['id']) {
    const { storageAccount } = this.wizardForm.value;

    this.destinationsFacade.getDestination(storageAccount?.id, destinationId).subscribe((destination) => {
      this.modalService.dismissAll();
      const modal = ftpStorageTypes.includes(storageAccount?.storageType)
        ? AddDestinationAlternateModalComponent
        : AddDestinationModalComponent;

      const modalInstance = this.modalService.openRef(modal, {
        size: MbsSize.md
      });
      modalInstance.componentInstance.storageAccount = storageAccount;
      modalInstance.componentInstance.destination = destination;

      modalInstance.result.catch(noop);
    });
  }

  private handleCredentialChanges() {
    this.wizardForm
      .get('credentialsInfo')
      .valueChanges.pipe(first(), untilDestroyed(this))
      .subscribe(() => {
        this.isCredentialsValid.set(false);
        this.isS3CompatibleChecked.set(false);
        this.isS3CompatibleOptionsValid.set(false);
      });

    this.accountDisplayName.valueChanges
      .pipe(
        filter((val) => val !== this.storageAccount?.value?.displayName),
        first(),
        untilDestroyed(this)
      )
      .subscribe(() => this.isCredentialsValid.set(false));
  }

  public ubAWSShowCreditsStateHandler(storageType: StorageType): void {
    this.selectStorageError.set(null);
    this.isLoading.set(true);
    this.ubSubscribeService
      .getCurrentCredits(storageType)
      .pipe(
        switchMap((credits) => {
          if (!credits) return of(null);

          if (!credits?.licenseCount) {
            // Zero discounted licenses still needs the PUT — it is what cancels the special pricing.
            // An admin is not allowed to cancel it: the backend answers 403
            return this.ubSubscribeService.spendCredits(storageType, true).pipe(
              switchMap((res) => {
                if (res?.paymentLink) {
                  window.location.href = res.paymentLink;
                }

                return of(null);
              }),
              catchError((err: HttpErrorResponse) => {
                this.isLoading.set(false);
                this.wizardForm.get('storageType').patchValue(null);

                if (err?.status === 403) {
                  this.selectStorageError.set(
                    this.i18N.transform('storages:addStorageAccountWizard.selectStorageStep.insufficientPermissions')
                  );
                } else {
                  this.toastService.error(err?.error?.detail || unidentifiedErrorText);
                }

                return EMPTY;
              })
            );
          }

          return of(credits);
        })
      )
      .subscribe({
        next: (credits) => {
          this.isLoading.set(false);
          if (!credits) return void this.wizard.changeStep(1);

          this.openUbAwsChangingPriceModal(credits, storageType);
        },
        error: () => this.isLoading.set(false)
      });
  }

  private openUbAwsChangingPriceModal(credits: CurrentCredits, storageType: StorageType): void {
    const modalRef = this.modalService.openRef(UbAwsChangingPriceModalComponent);
    modalRef.result
      .then((res) => {
        res && this.wizard.changeStep(1);
      })
      .catch(() => {
        this.wizardForm.get('storageType').patchValue(null);
      });
    const modal = modalRef.componentInstance as UbAwsChangingPriceModalComponent;

    modal.credits = credits;
    modal.storageType = storageType;
  }

  private setCompatibleError = (err: HttpErrorResponse) => {
    if (!err) return void this.compatibleError.set(null);

    err.headers?.get('trace-id');

    const { error } = err;

    if (error?.typeCode === CheckerError?.Timeout || error?.typeCode === CheckerError?.Unknown) {
      this.showS3AdditionalOptions.set(true);
    }

    this.compatibleError.set({
      type: error?.typeCode,
      text: this.errorMap[error?.typeCode] || error.detail,
      title: error.title,
      message: error.detail,
      url: err.url,
      traceId: err.headers?.get('trace-id')
    });
  };

  public reportIssue() {
    const modal = this.modalService.openRef(ReportProblemModalComponent);
    const error = this.compatibleError();
    (modal.componentInstance as ReportProblemModalComponent).initialErrorData = {
      title: error.title,
      message: error.message,
      url: error.url,
      traceId: error.traceId
    };
    modal.result.catch(noop);
  }

  public tryAgain() {
    this.showS3AdditionalOptions.set(false);
    this.s3CompatibleChecker();
  }
}
